import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Divider,
  Grid,
  MenuItem,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

function AddPresetForm({
  initialParams,
  onCancel,
  onSave,
  presetNameProp = "",
  isEditMode = false,
}) {
  const [presetName, setPresetName] = useState(presetNameProp);
  const [presetNameError, setPresetNameError] = useState(false);
  const [customParams, setCustomParams] = useState(initialParams || {});

  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const parseValue = (val) => {
    if (val === "") return val;
    const num = Number(val);
    return Number.isNaN(num) ? val : num;
  };

  const handleChange = (groupKey, paramKey, field, value) => {
    setCustomParams((prev) => ({
      ...prev,
      [groupKey]: {
        ...prev[groupKey],
        [paramKey]: {
          ...prev[groupKey][paramKey],
          [field]: field === "optimize" ? value : parseValue(value),
        },
      },
    }));
  };

  const handleSubmit = () => {
    if (!presetName.trim()) {
      setPresetNameError(true);
      return;
    }
    setPresetNameError(false);
    onSave(presetName.trim().toLowerCase(), customParams);
  };

  useEffect(() => {
    setPresetName(presetNameProp || "");
    setCustomParams(initialParams);
  }, [presetNameProp, initialParams]);

  return (
    <Dialog
      open
      onClose={onCancel}
      fullScreen={isMobile}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        {isEditMode
          ? t("trainer.EditHyperparameter")
          : t("trainer.AddDefaultHyperparameter")}
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          required
          label={t("trainer.EnterHyperparameterName")}
          value={presetName}
          onChange={(e) => {
            setPresetName(e.target.value);
            if (presetNameError && e.target.value.trim()) {
              setPresetNameError(false);
            }
          }}
          error={presetNameError}
          helperText={presetNameError ? "Preset name is required." : ""}
          sx={{ mb: 3 }}
          disabled={isEditMode}
        />

        {Object.entries(customParams).map(([groupKey, groupParams]) => (
          <div key={groupKey} style={{ marginBottom: 24 }}>
            <Typography variant="subtitle1" gutterBottom>
              {groupKey}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {Object.entries(groupParams).map(([paramKey, param]) => (
              <Grid
                container
                spacing={2}
                alignItems="center"
                key={`${groupKey}-${paramKey}`}
                sx={{ mb: 1 }}
              >
                <Grid item xs={12} sm={2}>
                  <Typography>{paramKey}</Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Min"
                    value={param.min}
                    size="small"
                    fullWidth
                    onChange={(e) =>
                      handleChange(groupKey, paramKey, "min", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Max"
                    value={param.max}
                    size="small"
                    fullWidth
                    onChange={(e) =>
                      handleChange(groupKey, paramKey, "max", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Init"
                    value={param.init}
                    size="small"
                    fullWidth
                    onChange={(e) =>
                      handleChange(groupKey, paramKey, "init", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Type"
                    value={param.type}
                    size="small"
                    fullWidth
                    select
                    disabled
                  >
                    <MenuItem value="int">int</MenuItem>
                    <MenuItem value="float">float</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={1}>
                  <TextField
                    label="Scale"
                    value={param.scale}
                    size="small"
                    fullWidth
                    select
                    disabled
                  >
                    <MenuItem value="lin">lin</MenuItem>
                    <MenuItem value="exp">exp</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={param.optimize}
                        onChange={(e) =>
                          handleChange(
                            groupKey,
                            paramKey,
                            "optimize",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Optimize"
                  />
                </Grid>
              </Grid>
            ))}
          </div>
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} variant="outlined" color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {t("trainer.Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddPresetForm;
